import config from "../config.json";
import { Params } from "../types/table/interfaces.type";
import { fetchCurrency } from "./CurrencyService";
import { fetchProfile } from "./ProfilesService";
import { createAxiosInstance } from "./ServicesHelpers";
import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import qs from "qs";

const axiosInstance = createAxiosInstance(config.METRICS_API_BASE_URL);

export enum CdrStatus {
  PENDING,
  DONE,
  ERROR,
  FAILED,
  CLIENT_NOT_RESPONDING,
  PENDING_CLIENT_DLR,
  CLIENT_REJECTED,
}

export enum DeliveryReceiptState {
  ENROUTE,
  DELIVERED,
  EXPIRED,
  DELETED,
  UNDELIVERABLE,
  ACCEPTED,
  REJECTED,
}

export interface IMonitor {
  id: number;
  name: string;
  count: number;
  delivered: number;
  deliveryPercentage?: number;
  totalCost: number;
  totalRate: number;
  totalProfit: number;
  totalConvertedRoutingCost?: number;
  totalConvertedMnpCost?: number;
  totalConvertedHlrCost?: number;
  employeeName?: string;
  profile?: string;
}

export interface IMonitorById {
  id: number;
  operatorName: string;
  operatorId: number;
  countryName: string;
  relativeConnectionProfileName: string;
  count: number;
  delivered: number;
  deliveredPercentage: number;
  totalCost: number;
  totalRate: number;
  totalProfit: number;
  cost: number;
  rate: number;
  profit: number;
  totalConvertedRoutingCost?: number;
  totalConvertedMnpCost?: number;
  totalConvertedHlrCost?: number;
  totalOriginalRoutingCost?: number;
  totalOriginalMnpCost?: number;
  totalOriginalHlrCost?: number;
  convertedRoutingCost?: number;
  convertedMnpCost?: number;
  convertedHlrCost?: number;
  originalRoutingCost?: number;
  originalMnpCost?: number;
  originalHlrCost?: number;
  originalCostCurrency?: string;
  mnpCostCurrency?: string;
  hlrCostCurrency?: string;
  convertedCostCurrency?: string;
  mnpProviderId?: number | null;
  mnpProviderName?: string | null;
  hlrProviderId?: number | null;
  hlrProviderName?: string | null;
  ratePerDelivery?: boolean;
  costPerDelivery?: boolean;
}

export async function fetchMonitor(
  params: Params<IMonitor & { profileType: string }>,
): Promise<IMonitor[]> {
  try {
    const modifiedParams = {
      ...params,
      profileType: params.profileType || "CLIENT",
    };
    const response = await axiosInstance.get("/monitor/data", {
      params: modifiedParams,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the cdrs.",
    );
  }
}

export const useMonitorQuery = (
  params: Params<IMonitor & { profileType?: string }>,
  enabled: boolean = true,
): UseQueryResult<IMonitor[], Error> => {
  const queryKey: [string, Params<IMonitor & { profileType?: string }>] = [
    "monitor",
    { ...params, profileType: params.profileType || "CLIENT" },
  ];

  return useQuery<
    IMonitor[],
    Error,
    IMonitor[],
    [string, Params<IMonitor & { profileType?: string }>]
  >({
    queryKey,
    queryFn: () =>
      fetchMonitor({ ...params, profileType: params.profileType || "CLIENT" }),
    enabled,
    placeholderData: keepPreviousData,
    gcTime: 60_000,
  });
};

export const fetchMonitorById = async (
  params: Params<
    IMonitor & { profileType: string; connectionProfileId?: string }
  >,
  id: IMonitor["id"],
): Promise<IMonitorById[]> => {
  try {
    const response = await axiosInstance.get(`/monitor/${id}`, {
      params: { ...params },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching the monitor list for ${id}.`,
    );
  }
};

export interface TransformedRow {
  totalCost: string;
  totalRate: string;
  profit: string;
}

export const getTransformedRowQueryOptions = (
  t: IMonitor & { profileId?: string },
  field: "totalCost" | "totalRate" | "profit",
): UseQueryOptions<string, Error> => ({
  queryKey: ["CurrencyName", t.profileId || t.id, field],
  queryFn: async (): Promise<string> => {
    const profileId = t.profileId || t.id;
    const profile = await fetchProfile(String(profileId));
    const currency = await fetchCurrency(profile.currencyId);
    const currencyName = currency?.name || "";

    if (field === "totalCost") {
      return `${t.totalCost || 0} ${currencyName}`;
    }
    if (field === "totalRate") {
      return `${t.totalRate || 0} ${currencyName}`;
    }
    if (field === "profit") {
      return `${(t.totalRate || 0) - (t.totalCost || 0)} ${currencyName}`;
    }
    return "";
  },
});

export function exportTableToExcel<T>(
  data: T[],
  fields: { key: string; header: string }[],
  fileName = "TableExport.xlsx",
) {
  const worksheetData = [
    fields.map((f) => f.header),
    ...data.map((row) => fields.map((f) => (row as any)[f.key])),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  if (worksheet["!ref"]) {
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let rowIdx = 1; rowIdx <= range.e.r; ++rowIdx) {
      const nameColIdx = fields.findIndex((f) => f.key === "name");
      if (nameColIdx === -1) break;
      const nameCellAddress = XLSX.utils.encode_cell({
        r: rowIdx,
        c: nameColIdx,
      });
      const nameCell = worksheet[nameCellAddress];
      if (nameCell && nameCell.v === "Test Client Connection 1") {
        for (let col = range.s.c; col <= range.e.c; ++col) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIdx, c: col });
          if (!worksheet[cellAddress]) continue;
          worksheet[cellAddress].s = {
            fill: {
              patternType: "solid",
              fgColor: { rgb: "FFFF00" },
            },
          };
        }
      }
    }
  }
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, fileName);
}
